// Recruiting — typed client for POST/PATCH /api/v1/recruiting/* (the ACTION
// forms under components/recruiting/actions/). Code against this contract
// exactly; the server is being built alongside this file (same discipline as
// lib/recruitingApi.ts's own header comment, and lib/moneyWrites.ts's for the
// Money page's writes).
//
// Every call rides authedWrite (lib/apiClient.ts), which attaches the bearer
// token AND mints a fresh Idempotency-Key per call — so a form's "Save" simply
// calling one of these functions again on retry already gets a new key per
// attempt, with no extra plumbing needed here (apiClient.ts's own header
// comment: "generated here rather than by the caller, so a page cannot forget
// it"). None of these functions accept a `key` — a caller that genuinely needs
// to reuse one (none does today) would call authedWrite directly instead.
import { authedWrite, ApiError } from './apiClient';
import type {
  CandidateDetail, CandidateLogEntry, CandidateFile, RecruitingTemplate, CandidateEmailEntry,
} from './recruitingApi';

/** The richer candidate shape the card/actions work from — CandidateRow plus
 * the address/city/state/zip/summary/createdAt fields only the detail fetch
 * carries. One copy, re-exported from here rather than redeclared, so the
 * actions tree and recruitingApi.ts can never drift into two shapes for the
 * same row. */
export type Candidate = CandidateDetail['candidate'];

export interface CreateCandidateInput {
  firstName: string;
  lastName: string;
  middleName?: string;
  email?: string;
  phone?: string;
  phoneAlt?: string;
  licenseNumber?: string;
  position?: string;
  homeLocationCode?: string;
  hiringManager?: string;
  source?: string;
  receivedAt?: string;
  note?: string;
}

export function createCandidate(input: CreateCandidateInput): Promise<{ candidate: Candidate }> {
  return authedWrite<{ candidate: Candidate }>('POST', '/api/v1/recruiting/candidates', input);
}

export interface UpdateCandidateInput {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  email?: string;
  // CandidateRow.emailRelay (lib/recruitingApi.ts) changed from `boolean` to
  // `string | null` under a concurrent edit to that file while this build was
  // in progress — a side effect of two trees building in parallel, not a
  // choice made here. No form in this tree writes emailRelay (it is not one
  // of the Edit fields the build brief lists); typed to match the CURRENT
  // read shape purely so the two files do not silently disagree. Re-check
  // against lib/recruitingApi.ts once something actually writes it.
  emailRelay?: string | null;
  phone?: string;
  phoneAlt?: string;
  licenseNumber?: string;
  position?: string;
  homeLocationCode?: string;
  hiringManager?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  source?: string;
}

export function updateCandidate(id: number, input: UpdateCandidateInput): Promise<{ candidate: Candidate }> {
  return authedWrite<{ candidate: Candidate }>('PATCH', `/api/v1/recruiting/candidates/${id}`, input);
}

/** The log kinds an ACTION form may write. EMAIL_IN/STAGE/OUTCOME/DO_NOT_CONTACT/
 * HIRE/SYSTEM/MIGRATED (lib/recruitingApi.ts's LOG_KIND_LABEL) are all system-
 * written, server-side, as a side effect of the other endpoints below — never
 * posted directly by this form. */
export type LogKind = 'NOTE' | 'CALL' | 'TEXT' | 'EMAIL_OUT' | 'SCORECARD';

export function addCandidateLog(
  id: number,
  kind: LogKind,
  body: string,
  meta?: Record<string, unknown>,
): Promise<{ log: CandidateLogEntry }> {
  const payload: Record<string, unknown> = { kind, body };
  if (meta) payload.meta = meta;
  return authedWrite<{ log: CandidateLogEntry }>('POST', `/api/v1/recruiting/candidates/${id}/log`, payload);
}

/**
 * Move stage. `reopen` is omitted entirely on a normal attempt — only the
 * explicit retry (the "Reopen and move" chip, after a 409) sends it — so the
 * first attempt's body is always exactly `{ stage, note }`, never `{ stage,
 * note, reopen: false }` or similar noise the server was never asked for.
 */
export function moveCandidateStage(
  id: number,
  stage: string,
  note: string,
  reopen?: boolean,
): Promise<{ candidate: Candidate }> {
  const payload: Record<string, unknown> = { stage, note };
  if (reopen) payload.reopen = true;
  return authedWrite<{ candidate: Candidate }>('POST', `/api/v1/recruiting/candidates/${id}/stage`, payload);
}

export interface SendToManagerResult {
  ok: boolean;
  stage: string | null;
  homeLocationCode: string | null;
  mail: { sent: boolean; to?: string[]; reason?: string };
}

/** Owner/director: tell a clinic's manager about a candidate, with a note;
 * optionally move the candidate to that clinic and/or back onto the hire list.
 * A mail failure comes back as mail.sent=false (the note is still saved). */
export function sendToManager(
  id: number,
  input: { clinic: string; note: string; moveToClinic?: boolean; putBack?: boolean; stage?: string },
): Promise<SendToManagerResult> {
  return authedWrite<SendToManagerResult>('POST', `/api/v1/recruiting/candidates/${id}/send-to-manager`, input);
}

export function setCandidateOutcome(
  id: number,
  outcome: string,
  reason: string,
): Promise<{ candidate: Candidate }> {
  return authedWrite<{ candidate: Candidate }>('POST', `/api/v1/recruiting/candidates/${id}/outcome`, { outcome, reason });
}

export function setDoNotContact(
  id: number,
  on: boolean,
  reason: string,
): Promise<{ candidate: Candidate }> {
  return authedWrite<{ candidate: Candidate }>('POST', `/api/v1/recruiting/candidates/${id}/do-not-contact`, { on, reason });
}

export function attachCandidateFile(
  id: number,
  url: string,
  name?: string,
  kind?: string,
): Promise<{ file: CandidateFile }> {
  const payload: Record<string, unknown> = { url };
  if (name) payload.name = name;
  if (kind) payload.kind = kind;
  return authedWrite<{ file: CandidateFile }>('POST', `/api/v1/recruiting/candidates/${id}/files`, payload);
}

export function deleteCandidateFile(id: number, fileId: number): Promise<{ file: CandidateFile }> {
  return authedWrite<{ file: CandidateFile }>('POST', `/api/v1/recruiting/candidates/${id}/files/${fileId}/delete`);
}

/**
 * A create/update refused because the person is already on file. Reads the
 * 409 body defensively (SPEC-001 discipline: "anything that cannot be proven
 * is raised, not assumed") — a 409 whose body does NOT actually carry a
 * numeric `existingCandidateId` returns `null` rather than handing a caller
 * `undefined` to silently navigate on, so the form falls back to showing the
 * server's own error text instead.
 */
export function existingCandidateConflict(
  err: unknown,
): { existingCandidateId: number; doNotContact: boolean } | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const id = err.body.existingCandidateId;
  if (typeof id !== 'number') return null;
  return { existingCandidateId: id, doNotContact: Boolean(err.body.doNotContact) };
}

// --- templates + email (docs/RECRUITING-PLAN.md item 5) --------------------

export interface CreateTemplateInput {
  scope: 'COMPANY' | 'MINE';
  name: string;
  subject: string;
  body: string;
}

export function createRecruitingTemplate(input: CreateTemplateInput): Promise<{ template: RecruitingTemplate }> {
  return authedWrite<{ template: RecruitingTemplate }>('POST', '/api/v1/recruiting/templates', input);
}

export interface UpdateTemplateInput {
  name?: string;
  subject?: string;
  body?: string;
}

export function updateRecruitingTemplate(
  id: number,
  input: UpdateTemplateInput,
): Promise<{ template: RecruitingTemplate }> {
  return authedWrite<{ template: RecruitingTemplate }>('PATCH', `/api/v1/recruiting/templates/${id}`, input);
}

export function removeRecruitingTemplate(id: number): Promise<{ template: RecruitingTemplate }> {
  return authedWrite<{ template: RecruitingTemplate }>('DELETE', `/api/v1/recruiting/templates/${id}`);
}

/**
 * Sends the email AND logs it server-side (src/recruiting_email.js) —
 * `sent`/`logged` are always both read from the response so a form can tell
 * "sent but not logged yet" (a 200, never thrown) from a genuine refusal
 * (an ApiError, e.g. the do-not-contact wall or a missing scope grant) apart.
 */
export interface SendCandidateEmailInput {
  to: string;
  subject: string;
  body: string;
  templateId?: number;
}

export interface SendCandidateEmailResult {
  sent: boolean;
  logged: boolean;
  email?: CandidateEmailEntry;
  gmailMessageId?: string;
  error?: string;
}

export function sendCandidateEmail(id: number, input: SendCandidateEmailInput): Promise<SendCandidateEmailResult> {
  return authedWrite<SendCandidateEmailResult>('POST', `/api/v1/recruiting/candidates/${id}/email`, input);
}

/** POST /api/v1/recruiting/fix-wording — not a versioned write (nothing is
 * persisted, no Idempotency-Key), so this rides `authedWrite` only for its
 * bearer-token plumbing; the extra header costs nothing and the server
 * simply ignores it. */
export function fixRecruitingWording(text: string): Promise<{ text: string }> {
  return authedWrite<{ text: string }>('POST', '/api/v1/recruiting/fix-wording', { text });
}

// --- the rest of nvRecruiter (docs/RECRUITING-PLAN.md; owner, 2026-09-27) --

/** OWNER/DIRECTOR only (server-enforced; this chip does not even render for
 * anyone else — CandidateActions.tsx). A 409 means the merge could not be
 * completed (a race — the duplicate changed underneath); the form surfaces
 * the server's own message rather than guessing why. */
export function mergeCandidate(
  id: number,
  duplicateId: number,
  reason: string,
): Promise<{ candidate: Candidate; mergedDuplicateId: number }> {
  return authedWrite('POST', `/api/v1/recruiting/candidates/${id}/merge`, { duplicateId, reason });
}

/** `referredByEmployeeId: null` clears the referral. */
export function setCandidateReferral(
  id: number,
  referredByEmployeeId: number | null,
  note?: string,
): Promise<{ candidate: Candidate }> {
  return authedWrite('POST', `/api/v1/recruiting/candidates/${id}/referral`, { referredByEmployeeId, note });
}

/** What the manager saw on the Florida DOH site (owner, 2026-09-28: the only
 * license check is the one a manager does by hand for someone we intend to
 * interview). The app never looks a license up itself. */
export type LicenseCheckedStatus = 'VALID' | 'EXPIRED' | 'NOT_FOUND' | 'DISCIPLINED';

export function markLicenseChecked(
  id: number,
  input: { licenseStatus: LicenseCheckedStatus; licenseExpires?: string; note?: string },
): Promise<{ candidate: Candidate }> {
  return authedWrite('POST', `/api/v1/recruiting/candidates/${id}/license-checked`, input);
}

// --- nvRecruiter cleanup (owner, 2026-10-11; src/recruiting_cleanup.js) ----

/** Mark one of this candidate's files as the current resume; `null` clears
 * the choice (the newest resume shows again). Anyone who may edit the
 * candidate (server-enforced). */
export function setCurrentResume(
  id: number,
  fileId: number | null,
): Promise<{ candidate: { id: number; currentResumeFileId: number | null }; unchanged?: boolean }> {
  return authedWrite('POST', `/api/v1/recruiting/candidates/${id}/current-resume`, { fileId });
}

/** "Not the same person" on the same-name review list — OWNER/DIRECTOR only
 * (server-enforced). Remembered: the group stops showing until a new record
 * with that name appears. */
export function dismissSameNameGroup(
  candidateIds: number[],
  reason?: string,
): Promise<{ review: { id: number; candidateIds: number[] }; alreadyAnswered?: boolean }> {
  const payload: Record<string, unknown> = { candidateIds };
  if (reason && reason.trim()) payload.reason = reason.trim();
  return authedWrite('POST', '/api/v1/recruiting/same-name/dismiss', payload);
}
